import React, {useMemo, useState} from 'react';
import MediaThumbnail from './MediaThumbnail';
import PropTypes from 'prop-types';
import {
  ButtonWithIcon,
  CardSkeletonLoader,
  LanguageTabs
} from '@/components/atoms';
import {Accordion, AccordionItem} from '@/components/molecules';
import DuplicateAlert from '@/components/atoms/Toast/DuplicateAlert';
import {ROUTES} from '@/utils/constants/routesConstants';
import MediaPreviewModal from './MediaPreviewModal';
import {getFileNameFromUrl} from '@/helpers/shadcn/utils';
import useWheelOfFortuneReview from '@/hooks/useWheelOfFortuneReview';
import WofQuestionsTable from '@/containers/Activity/WheelOfFortuneAttributes/WofQuestionsTable';
import {
  GameReviewGridRow,
  GameReviewSingleRow,
  GameSectionTitle
} from './GameReviewComponents';
import {buildGameAvailabilityItems, GAME_SPACING} from './gameReviewUtils';
import ActivitySegmentsReviewSection from '@/containers/Activity/ActivityReview/components/ActivitySegmentsReviewSection';

const WheelOfFortuneGameReview = ({
  loading,
  showToast,
  toastMessage,
  handleToastClose,
  handleViewActivity,
  isEditable,
  editButtonSchema,
  navigate,
  activityFormData,
  isExcluded,
  getFormattedDate
}) => {
  const {
    gameDisplayTabIndex,
    setGameDisplayTabIndex,
    gameSettingsTabIndex,
    setGameSettingsTabIndex,
    previewModal,
    handlePreview,
    handleClosePreview,
    asset,
    languages,
    languageCodes,
    gameDisplayTranslation,
    gameSettingsTranslation,
    slices,
    equalProbability,
    sliceProbabilities,
    slicePoints,
    quizLanguageCodes
  } = useWheelOfFortuneReview(activityFormData);
  const [attributesTabIndex, setAttributesTabIndex] = useState(0);

  const activeAttributesLangCode =
    quizLanguageCodes[attributesTabIndex] || 'en';

  const recordsForTable = useMemo(() => {
    const quizQuestions = activityFormData?.wofQuizQuestions?.data || [];
    return quizQuestions.map((question) => {
      const translation =
        question.translations?.[activeAttributesLangCode] || {};
      return {
        question: translation.question || '',
        rightAnswer: translation.rightAnswer || '',
        wrongAnswer1: translation.wrongAnswer1 || '',
        wrongAnswer2: translation.wrongAnswer2 || '',
        wrongAnswer3: translation.wrongAnswer3 || ''
      };
    });
  }, [activityFormData, activeAttributesLangCode]);

  if (!activityFormData || Object.keys(activityFormData).length === 0) {
    return (
      <div className='wheel-of-fortune-game-review-container w-full'>
        <CardSkeletonLoader count={3} />
      </div>
    );
  }

  return (
    <div
      data-testid='wheel-of-fortune-game-review'
      className='wheel-of-fortune-game-review-container w-full'>
      {loading && <CardSkeletonLoader count={3} />}
      {!loading && (
        <>
          {showToast && (
            <DuplicateAlert
              type='info'
              message={toastMessage}
              show={true}
              onCloseClick={handleToastClose}
              handleClick={handleViewActivity}
              buttonText='View Activity'
            />
          )}
          <div className='wheel-of-fortune-game-review mt-2 w-full'>
            <Accordion
              isDark
              size='large'
              content={
                <div className={`${GAME_SPACING.accordionGapClass} w-full`}>
                  {/* Setup Accordion */}
                  <AccordionItem
                    isDark
                    expanded
                    label='Setup'
                    rightAccessoryChild={
                      isEditable ? (
                        <ButtonWithIcon
                          uiSchema={editButtonSchema}
                          iconLeading='edit'
                          handleButton={() => navigate(ROUTES.ACTIVITY_ADD)}
                        />
                      ) : null
                    }
                    content={
                      <div className='w-full'>
                        <GameSectionTitle
                          label='Activity'
                          className='mb-3'
                          showDivider
                        />
                        <GameReviewGridRow
                          dataTestId='wheel-setup-grid-row'
                          preserveRhythm
                          items={[
                            {
                              label: 'Activity Type',
                              value: activityFormData?.activityType
                                ? activityFormData.activityType
                                    .split(' ')
                                    .map(
                                      (w) =>
                                        w.charAt(0).toUpperCase() +
                                        w.slice(1).toLowerCase()
                                    )
                                    .join(' ')
                                : '-'
                            },
                            {
                              label: 'Activity Name',
                              value: activityFormData?.activityName || '-'
                            },
                            {
                              label: 'Budget',
                              value: activityFormData?.pointsEarned || '-'
                            },
                            {
                              label: 'Budget Period',
                              value: activityFormData?.budgetPeriod || '-'
                            }
                          ]}
                        />

                        <GameSectionTitle
                          label='Availability'
                          className='mb-3 mt-6'
                          showDivider
                        />
                        <GameReviewGridRow
                          dataTestId='wheel-availability-grid-row'
                          items={buildGameAvailabilityItems(
                            activityFormData,
                            getFormattedDate
                          )}
                        />

                        <ActivitySegmentsReviewSection
                          activityFormData={activityFormData}
                          isExcluded={isExcluded}
                          RowComponent={GameReviewSingleRow}
                          SectionTitleComponent={GameSectionTitle}
                          testIdPrefix='wheel'
                          showAllCustomersRow
                          showExcludedRow
                        />
                      </div>
                    }
                  />

                  {/* Display Accordion */}
                  <AccordionItem
                    isDark
                    expanded
                    label='Display'
                    rightAccessoryChild={
                      isEditable ? (
                        <ButtonWithIcon
                          uiSchema={editButtonSchema}
                          iconLeading='edit'
                          handleButton={() => navigate(ROUTES.ACTIVITY_DISPLAY)}
                        />
                      ) : null
                    }
                    content={
                      <div className='w-full'>
                        <GameSectionTitle
                          label='Game Display'
                          className='mb-4'
                        />

                        {/* Game Image Section */}
                        <div className='mb-6'>
                          <p className='mb-3 text-sm font-bold text-white'>
                            Game Image
                          </p>

                          <div
                            className='flex items-start gap-6'
                            data-testid='game-image-thumbnail-row'>
                            {asset?.gameImage ? (
                              <MediaThumbnail
                                url={asset.gameImage}
                                alt='game-image'
                                type='image'
                                onPreview={() =>
                                  handlePreview(
                                    asset.gameImage,
                                    'image',
                                    'Game Image Preview'
                                  )
                                }
                              />
                            ) : (
                              <div className='flex h-[100px] w-[100px] items-center justify-center rounded border border-gray-600 bg-gray-800'>
                                <p className='text-xs text-gray-500'>
                                  No Image
                                </p>
                              </div>
                            )}

                            <div>
                              <p className='text-sm text-[#D0D0D0]'>
                                Game Image
                              </p>
                              <p className='font-bold text-white'>
                                {asset?.gameImage
                                  ? getFileNameFromUrl(asset.gameImage)
                                  : '-'}
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* Language Tabs */}
                        {languageCodes.length > 0 && (
                          <div className='mb-4'>
                            <LanguageTabs
                              languages={languageCodes}
                              fullLanguages={languages}
                              activeTabIndex={gameDisplayTabIndex}
                              setActiveTabIndex={setGameDisplayTabIndex}
                              testIdPrefix='review-game-display-tab'
                            />
                          </div>
                        )}

                        {/* Name and Tag Description */}
                        <GameReviewGridRow
                          dataTestId='wheel-display-name-grid-row'
                          items={[
                            {
                              label: 'Name',
                              value:
                                gameDisplayTranslation?.name ||
                                gameDisplayTranslation?.title ||
                                '-'
                            },
                            {
                              label: 'Tag Description',
                              value:
                                gameDisplayTranslation.tagDescription || '-'
                            }
                          ]}
                        />

                        <div className='mt-6 border-b border-gray-700' />

                        {/* Game Settings Section */}
                        <div className='mb-6 mt-6'>
                          <GameSectionTitle
                            label='Game Settings'
                            className='mb-4'
                          />

                          {/* Slices, Probability, Points Row */}
                          <GameReviewGridRow
                            dataTestId='wheel-game-settings-grid-row'
                            items={[
                              {
                                label: 'Slices',
                                value: slices
                              },
                              {
                                label: 'Probability',
                                value:
                                  sliceProbabilities.length > 0
                                    ? sliceProbabilities
                                        .map((p) => `${p}%`)
                                        .join(', ')
                                    : equalProbability
                                      ? Array.from(
                                          {length: slices},
                                          () =>
                                            `${Number((100 / slices).toFixed(1))}%`
                                        ).join(', ')
                                      : '-'
                              },
                              {
                                label: 'Points',
                                value:
                                  slicePoints.length > 0
                                    ? slicePoints.join(', ')
                                    : '-'
                              }
                            ]}
                          />

                          {/* Language Tabs */}
                          {languageCodes.length > 0 && (
                            <div className='mb-4'>
                              <LanguageTabs
                                languages={languageCodes}
                                fullLanguages={languages}
                                activeTabIndex={gameSettingsTabIndex}
                                setActiveTabIndex={setGameSettingsTabIndex}
                                testIdPrefix='review-game-settings-tab'
                              />
                            </div>
                          )}

                          {/* Points (Unit of Measure) and No Points Awarded */}
                          <GameReviewGridRow
                            dataTestId='wheel-game-settings-lang-grid-row'
                            items={[
                              {
                                label: 'Points (Unit of Measure)',
                                value:
                                  gameSettingsTranslation?.pointsUnitOfMeasure ||
                                  '-'
                              },
                              {
                                label: 'No Points Awarded',
                                value:
                                  gameSettingsTranslation?.noPointsAwarded ||
                                  '-'
                              }
                            ]}
                          />
                        </div>
                      </div>
                    }
                  />

                  {/* Attributes Accordion - Empty Placeholder */}
                  <AccordionItem
                    isDark
                    expanded
                    label='Attributes'
                    rightAccessoryChild={
                      isEditable ? (
                        <ButtonWithIcon
                          uiSchema={editButtonSchema}
                          iconLeading='edit'
                          handleButton={() =>
                            navigate(ROUTES.ACTIVITY_ATTRIBUTES)
                          }
                        />
                      ) : null
                    }
                    content={
                      <div className='w-full'>
                        {quizLanguageCodes.length > 0 && (
                          <div className='mb-4'>
                            <LanguageTabs
                              languages={quizLanguageCodes}
                              fullLanguages={languages}
                              activeTabIndex={attributesTabIndex}
                              setActiveTabIndex={setAttributesTabIndex}
                              testIdPrefix='review-attributes-tab'
                            />
                          </div>
                        )}
                        <GameSectionTitle label='Questions' className='mb-4' />
                        <WofQuestionsTable data={recordsForTable} />
                      </div>
                    }
                  />
                </div>
              }
            />
          </div>

          <MediaPreviewModal
            showModal={previewModal.show}
            onClose={handleClosePreview}
            mediaUrl={previewModal.url}
            mediaType={previewModal.type}
            title={previewModal.title}
          />
        </>
      )}
    </div>
  );
};

WheelOfFortuneGameReview.propTypes = {
  loading: PropTypes.bool,
  showToast: PropTypes.bool,
  toastMessage: PropTypes.string,
  handleToastClose: PropTypes.func,
  handleViewActivity: PropTypes.func,
  isEditable: PropTypes.bool,
  editButtonSchema: PropTypes.object,
  navigate: PropTypes.func.isRequired,
  activityFormData: PropTypes.object,
  isExcluded: PropTypes.bool,
  getFormattedDate: PropTypes.func.isRequired
};

export default React.memo(WheelOfFortuneGameReview);
